import React, { useState, useEffect, useContext } from 'react';
import { Table, Card, Typography, Spin, message, Input, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import LandingLayout from './LandingLayout';
import { ThemeContext } from '../../context/ThemeContext';
import apiClient from '../../services/apiClient';

const { Title } = Typography;

const PromoterShareholdingsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const { isDark } = useContext(ThemeContext);

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  useEffect(() => {
    fetchAllScores();
  }, []);

  const fetchAllScores = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/shareholding-pattern/shareholding-promoter/all`);
      if (response.status === 200) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching promoter scores:', error);
      message.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const renderPoint = (val) => {
    if (val === -1) {
      return <Tag color="volcano">Data Not Available</Tag>;
    }
    if (val === -2) {
      return <Tag color="purple">Financial Not Available</Tag>;
    }
    if (val === null || val === undefined) return '-';
    
    // Numeric points color coding
    let color = 'orange';
    if (val > 75) color = 'green';
    else if (val > 50) color = 'blue';
    
    return (
      <Tag color={color}>
        {typeof val === 'number' ? val.toFixed(2) : val}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Issuer',
      dataIndex: 'issuer',
      key: 'issuer',
      fixed: 'left',
      width: 250,
      sorter: (a, b) => a.issuer.localeCompare(b.issuer),
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: 'Total Score',
      dataIndex: 'score',
      key: 'score',
      width: 120,
      sorter: (a, b) => (a.score || 0) - (b.score || 0),
      render: (score) => (
        <Tag color={score > 75 ? 'green' : score > 50 ? 'blue' : 'orange'} style={{ fontWeight: 'bold' }}>
          {score?.toFixed(2)}
        </Tag>
      ),
    },
    {
      title: 'Governance Score',
      dataIndex: 'governanceScore',
      key: 'governanceScore',
      width: 150,
      sorter: (a, b) => (a.governanceScore || 0) - (b.governanceScore || 0),
      render: (val) => val?.toFixed(2),
    },
    {
      title: 'Promoter Score',
      dataIndex: 'promoterScore',
      key: 'promoterScore',
      width: 150,
      sorter: (a, b) => (a.promoterScore || 0) - (b.promoterScore || 0),
      render: (val) => val?.toFixed(2),
    },
    {
      title: 'Mgmt Points',
      dataIndex: 'managementPoint',
      key: 'managementPoint',
      width: 140,
      render: (val) => renderPoint(val),
    },
    {
      title: 'Pledge Points',
      dataIndex: 'pledgePoint',
      key: 'pledgePoint',
      width: 140,
      render: (val) => renderPoint(val),
    },
    {
      title: 'Defaulter Points',
      dataIndex: 'defaulterPoint',
      key: 'defaulterPoint',
      width: 140,
      render: (val) => renderPoint(val),
    },
    {
      title: 'RPT Points',
      dataIndex: 'rptPoint',
      key: 'rptPoint',
      width: 180,
      render: (val) => renderPoint(val),
    },
    {
      title: 'Fin RPT Points',
      dataIndex: 'frptPoint',
      key: 'frptPoint',
      width: 180,
      render: (val) => renderPoint(val),
    },
    {
      title: 'Complexity Points',
      dataIndex: 'complexityPoint',
      key: 'complexityPoint',
      width: 180,
      render: (val) => renderPoint(val),
    },
    {
      title: 'Penalties Points',
      dataIndex: 'penaltiesPoint',
      key: 'penaltiesPoint',
      width: 180,
      render: (val) => renderPoint(val),
    },
    {
      title: 'Grievance Points',
      dataIndex: 'grievancePoint',
      key: 'grievancePoint',
      width: 180,
      render: (val) => renderPoint(val),
    },
  ];

  const filteredData = data.filter(item => 
    item.issuer?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <LandingLayout>
      <div style={{ padding: '24px', background: isDark ? '#14141f' : '#f0f2f5', minHeight: '100vh' }}>
        <Card 
          className="modern-section-card" 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <Title level={3} style={{ margin: 0 }}>Promoter Shareholdings All Scores</Title>
              <Input
                placeholder="Search Issuer"
                prefix={<SearchOutlined />}
                style={{ width: 300 }}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              dataSource={filteredData}
              columns={columns}
              rowKey="issuer"
              scroll={{ x: 1700, y: 'calc(100vh - 300px)' }}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} records`
              }}
              size="middle"
              bordered
            />
          )}
        </Card>
      </div>
    </LandingLayout>
  );
};

export default PromoterShareholdingsTable;
